import { useRef } from "react";
import { ArrowDown, ArrowUp, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import type { ProductImage } from "@/types/models";
import { useProductImages } from "@/pages/products/hooks/useProductImages";

const MAX_IMAGES = 10;
const ACCEPTED_TYPES = "image/jpeg,image/png,image/webp";

export interface ProductImageUploaderProps {
  productId: string;
  images: ProductImage[];
}

function sortByPosition(images: ProductImage[]) {
  return [...images].sort((a, b) => a.position - b.position);
}

export function ProductImageUploader({ productId, images }: ProductImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    attachImage,
    removeImage,
    reorderImages,
    attaching,
    removing,
    reordering,
    loading,
    error,
  } = useProductImages(productId);

  const sortedImages = sortByPosition(images);
  const atCap = sortedImages.length >= MAX_IMAGES;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    attachImage(file);
    event.target.value = "";
  };

  const handleRemove = (imageId: string) => {
    if (!window.confirm("Remover esta imagem?")) return;
    removeImage(imageId);
  };

  const moveImage = (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sortedImages.length) return;

    const ids = sortedImages.map((img) => img.id);
    [ids[index], ids[targetIndex]] = [ids[targetIndex], ids[index]];
    reorderImages(ids);
  };

  return (
    <section className="mt-12 max-w-2xl border-t border-primary-200 dark:border-primary-800 pt-10">
      <h2 className="text-xl font-light text-primary-900 dark:text-white mb-2">
        Imagens do produto
      </h2>
      <p className="text-sm font-light text-primary-600 dark:text-primary-400 mb-6">
        Até {MAX_IMAGES} imagens (JPEG, PNG ou WebP). A primeira é a principal.
      </p>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {atCap && (
        <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">
          Limite de {MAX_IMAGES} imagens atingido.
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        className="sr-only"
        data-testid="product-image-file-input"
        onChange={handleFileChange}
        disabled={atCap || loading}
      />

      <Button
        type="button"
        variant="outline"
        disabled={atCap || loading}
        onClick={() => fileInputRef.current?.click()}
        className="mb-8"
      >
        {attaching ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <ImagePlus className="mr-2 h-4 w-4" />
        )}
        Adicionar imagem
      </Button>

      {sortedImages.length === 0 ? (
        <p className="text-sm font-light text-primary-500 dark:text-primary-500">
          Nenhuma imagem cadastrada.
        </p>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {sortedImages.map((image, index) => {
            const imageSrc = image.url ?? image.thumbnailUrl;
            return (
            <li
              key={image.id}
              className="relative border border-primary-200 dark:border-primary-800 p-2"
            >
              <div className="aspect-square bg-primary-100 dark:bg-primary-900 flex items-center justify-center overflow-hidden">
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={(event) => {
                      const fallback = image.url ?? image.thumbnailUrl;
                      if (fallback && event.currentTarget.src !== fallback) {
                        event.currentTarget.src = fallback;
                      }
                    }}
                  />
                ) : (
                  <Loader2 className="h-8 w-8 animate-spin text-primary-400" aria-label="Processando imagem" />
                )}
              </div>

              {index === 0 && (
                <span
                  className={cn(
                    "absolute top-3 left-3 px-2 py-0.5 text-xs uppercase tracking-widest border",
                    "border-primary-900 dark:border-white text-primary-900 dark:text-white bg-white/90 dark:bg-primary-950/90"
                  )}
                >
                  Principal
                </span>
              )}

              <div className="mt-2 flex flex-wrap gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={index === 0 || reordering}
                  aria-label="Mover para cima"
                  onClick={() => moveImage(index, "up")}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={index === sortedImages.length - 1 || reordering}
                  aria-label="Mover para baixo"
                  onClick={() => moveImage(index, "down")}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  disabled={removing}
                  aria-label="Remover imagem"
                  onClick={() => handleRemove(image.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
